import { toast } from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { useCreateMedication } from "@/features/Patient/MedicalRecords/MedicationsAllergies/network/useMedications";
import { useState } from "react";
import { CiqMedicationPopover } from "./CiqMedicationPopover";
import { CiqMedicationSearch } from "./CiqMedicationSearch";
import { CiqMedicationSingleRow } from "./CiqMedicationSingleRow";
import type {
  CodifiedMedication,
  ProcessedMedication,
} from "./useAmazonVisitIntake";
import { useMarkMedicationAddedToChart } from "./useMarkMedicationAddedToChart";

interface CiqMedicationProps {
  medication: ProcessedMedication;
  patientId: string;
  encounterId?: string;
  questionnaireResponseId?: string;
  onMedicationAddSuccess?: (
    medication: ProcessedMedication,
  ) => void | Promise<void>;
}

export const CiqMedication = ({
  medication,
  patientId,
  encounterId,
  questionnaireResponseId,
  onMedicationAddSuccess,
}: CiqMedicationProps) => {
  const [addedDrug, setAddedDrug] = useState<CodifiedMedication | undefined>(
    medication.codifiedDrugs?.find((d) => d.addedToChartAt),
  );

  const {
    mutateAsync: createMedication,
    isPending,
    variables,
  } = useCreateMedication({ patientId });
  const { mutateAsync: markMedicationAddedToChart } =
    useMarkMedicationAddedToChart();

  const showDosageAndDrugForm = medication.dosage && medication.drugForm;
  const showFrequency = medication.frequencyDisplay;
  const showPrn = !showFrequency && medication.isPrn;
  const pendingDrugName = isPending
    ? (variables?.medication?.text ?? "")
    : undefined;

  const noCodedDrugs = !medication.codifiedDrugs?.length;
  const singleCodedDrug =
    medication.codifiedDrugs?.length === 1 ? medication.codifiedDrugs[0] : null;

  const handleAdd = async (drug: CodifiedMedication) => {
    try {
      await createMedication({
        subjectId: patientId,
        encounterId,
        medication: {
          coding: [
            {
              system: drug.drugCodeSystem,
              code: drug.drugCode,
              display: drug.drugName,
            },
          ],
          text: drug.drugName,
        },
        medicationStatement: {},
      });
      setAddedDrug(drug);
    } catch {
      toast.error(`Failed to add ${drug.drugName} to medications`);
      return;
    }

    if (onMedicationAddSuccess) {
      try {
        await onMedicationAddSuccess(medication);
      } catch {}
    }

    if (questionnaireResponseId) {
      try {
        await markMedicationAddedToChart({
          questionnaireResponseId,
          drugCode: drug.drugCode,
          drugCodeSystem: drug.drugCodeSystem,
        });
      } catch {}
    }
  };

  return (
    <div className="border-l-4 border-ai pl-2 py-0.5 rounded">
      <BodyText size="sm" bold>
        {medication.name}
        {medication.strength && <> {medication.strength}</>}
        {showDosageAndDrugForm && (
          <>
            {", "}
            {medication.dosage} {medication.drugForm}
          </>
        )}
        {showFrequency && (
          <>
            {", "}
            {medication.frequencyDisplay}
          </>
        )}
        {showPrn && <>{", as needed"}</>}
      </BodyText>
      {singleCodedDrug ? (
        <CiqMedicationSingleRow
          drug={singleCodedDrug}
          onAdd={handleAdd}
          isPending={isPending}
          isAdded={addedDrug?.drugCode === singleCodedDrug.drugCode}
        />
      ) : noCodedDrugs ? (
        <CiqMedicationSearch
          patientId={patientId}
          defaultSearchTerm={medication.name.split(" ")[0] ?? ""}
          onAdd={handleAdd}
          isPending={isPending}
          addedDrug={addedDrug}
          pendingDrugName={pendingDrugName}
        />
      ) : (
        <CiqMedicationPopover
          drugs={medication.codifiedDrugs}
          onAdd={handleAdd}
          isPending={isPending}
          addedDrug={addedDrug}
          pendingDrugName={pendingDrugName}
        />
      )}
    </div>
  );
};
